import { useEffect, useState } from "react";

import { drugReferenceService, lookupId } from "services/drugReferenceService";

/** Subscribe-free read of one cached document; `loading` is true until it settles. */
function useCachedDoc(id, read) {
  const [state, setState] = useState({ id: null, doc: null });

  useEffect(() => {
    if (!id) return undefined;
    let live = true;
    read(id).then((doc) => {
      if (live) setState({ id, doc });
    });
    return () => {
      live = false;
    };
  }, [id, read]);

  if (!id) return { doc: null, loading: false };
  // A result for a previous id is not this id's result.
  return state.id === id ? { doc: state.doc, loading: false } : { doc: null, loading: true };
}

// Decodes lookupId: "rxcui:6809-oral-pill" or "name:coumadin".
const readReference = (id) => {
  const value = id.slice(id.indexOf(":") + 1);
  return drugReferenceService.getReferenceFor(
    id.startsWith("rxcui:") ? { rxcui: value } : { name: value }
  );
};
const readLabel = (setId) => drugReferenceService.getLabel(setId);

/**
 * The drugReference entry for a medication (see functions/src/openfda/sync.js for its fields),
 * as `{ reference, loading }`: by its stored `rxcui`, else by its `name` through the brand-name
 * aliases.
 */
export function useDrugReference(rxcui, name) {
  const { doc, loading } = useCachedDoc(lookupId({ rxcui, name }), readReference);
  return { reference: doc, loading };
}

/** The trimmed openFDA label document for a set id, as `{ label, loading }`. */
export function useDrugLabel(setId) {
  const { doc, loading } = useCachedDoc(setId || null, readLabel);
  return { label: doc, loading };
}

const readAlias = (name) => drugReferenceService.getAlias(name);

/**
 * The drugAliases entry a hand-typed medication name matched (a brand from RxNorm or another
 * country's registry, an international name), as `{ alias, loading }`; null when it has an
 * rxcui or matched nothing.
 */
export function useDrugAlias(rxcui, name) {
  const { doc, loading } = useCachedDoc(!rxcui && name ? String(name) : null, readAlias);
  return { alias: doc, loading };
}
